import { useState, useMemo } from "react";
import { Modal, ModalHeading, ModalFooter, Button, Input } from "@biteship/atom";
import Select from "@components/input/Select";
import {
  COLLECTION_METHOD_LABELS,
  COLLECTION_METHOD_VALUES,
  SERVICE_TYPES_LABELS,
  SERVICE_TYPES_VALUES,
  SHIPPING_TYPE_LABELS,
  SHIPPING_TYPE_VALUES,
  SHIPMENT_DURATION_UNIT_LABELS,
  SHIPMENT_DURATION_UNIT_VALUES,
} from "../constants";
import { createOpenCourierService } from "@store/action/openCourier";

export default function ModalServiceForm({ show, onClose, openCourierId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  
  const initialFormState = {
    name: "",
    serviceCode: "",
    description: "",
    shippingType: SHIPPING_TYPE_VALUES[0],
    serviceType: SERVICE_TYPES_VALUES[0],
    shipmentDurationRange: "",
    shipmentDurationUnit: SHIPMENT_DURATION_UNIT_VALUES[0],
    collectionMethods: [COLLECTION_METHOD_VALUES[0]],
  };

  const [formData, setFormData] = useState(initialFormState);

  const makeOptions = (values, labels) => values.map((v) => ({ value: v, label: labels[v] || v }));

  const collectionMethodOptions = useMemo(
    () => makeOptions(COLLECTION_METHOD_VALUES, COLLECTION_METHOD_LABELS),
    []
  );
  const serviceTypeOptions = useMemo(
    () => makeOptions(SERVICE_TYPES_VALUES, SERVICE_TYPES_LABELS),
    []
  );
  const shippingTypeOptions = useMemo(
    () => makeOptions(SHIPPING_TYPE_VALUES, SHIPPING_TYPE_LABELS),
    []
  );
  const durationUnitOptions = useMemo(
    () => makeOptions(SHIPMENT_DURATION_UNIT_VALUES, SHIPMENT_DURATION_UNIT_LABELS),
    []
  );

  const handleFieldChange = (field) => (e) => {
    const value = e?.target?.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCodeChange = (e) => {
    const code = e?.target?.value;
    // remove all spaces and special characters except underscore
    const sanitized = code.replace(/[^a-zA-Z0-9_]/g, "");
    setFormData((prev) => ({ ...prev, serviceCode: sanitized }));
  };

  const handleShipmentDurationRangeChange = (e) => {
    const raw = e?.target?.value || "";
    const sanitized = raw.replace(/\s+/g, "").replace(/[^0-9-]/g, "");
    setFormData((prev) => ({ ...prev, shipmentDurationRange: sanitized }));
  };

  const handleCollectionMethodChange = (idx) => (e) => {
    const value = e?.target?.value;
    setFormData((prev) => {
      const next = Array.isArray(prev.collectionMethods) ? [...prev.collectionMethods] : [];
      next[idx] = value;
      return { ...prev, collectionMethods: next };
    });
  };

  const handleAddCollectionMethod = () => {
    setFormData((prev) => {
      const current = Array.isArray(prev.collectionMethods) ? prev.collectionMethods : [];
      if (current.length >= COLLECTION_METHOD_VALUES.length) return prev;
      const available = COLLECTION_METHOD_VALUES.find((v) => !current.includes(v)) || COLLECTION_METHOD_VALUES[0];
      return { ...prev, collectionMethods: [...current, available] };
    });
  };

  const handleRemoveCollectionMethod = (idx) => () => {
    setFormData((prev) => {
      const current = Array.isArray(prev.collectionMethods) ? [...prev.collectionMethods] : [];
      current.splice(idx, 1);
      return { ...prev, collectionMethods: current };
    });
  };

  const validateForm = () => {
    if (!formData.name || formData.name.trim() === "") {
      return { valid: false, message: "Nama layanan harus diisi" };
    }
    if (!formData.serviceCode || formData.serviceCode.trim() === "") {
      return { valid: false, message: "Kode layanan harus diisi" };
    }
    if (!formData.shippingType) {
      return { valid: false, message: "Tipe pengiriman harus dipilih" };
    }
    if (!formData.serviceType) {
      return { valid: false, message: "Tipe layanan harus dipilih" };
    }
    if (!formData.shipmentDurationRange || formData.shipmentDurationRange.trim() === "") {
      return { valid: false, message: "Rentang durasi pengiriman harus diisi" };
    }
    if (!formData.shipmentDurationUnit) {
      return { valid: false, message: "Satuan durasi harus dipilih" };
    }
    if (!Array.isArray(formData.collectionMethods) || formData.collectionMethods.length === 0) {
      return { valid: false, message: "Setidaknya satu metode pengambilan harus dipilih" };
    }
    return { valid: true };
  };

  const handleSubmit = async () => {
    const validation = validateForm();
    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    if (!window.confirm("Tambah layanan baru ini?")) return;

    setLoading(true);
    try {
      await createOpenCourierService(openCourierId, formData);
      
      alert("Layanan berhasil ditambahkan");
      setFormData(initialFormState);
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error adding service:", error);
      const errorMessage = error?.response?.data?.error || "Gagal menambahkan layanan. Silahkan coba kembali";
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormState);
    onClose();
  };

  return (
    <Modal onClose={handleClose} size="lg" show={show}>
      <ModalHeading
        title="Tambah Layanan"
        onClose={handleClose}
      />
      
      <div className="p-4 space-y-6">
        {/* Name */}
        <Input
          label="Nama Layanan"
          placeholder="Instant Bike"
          className="w-full"
          inputClassName="border-gray-300"
          value={formData.name}
          onChange={handleFieldChange("name")}
        />

        {/* Service Code */}
        <div>
          <Input
            label="Kode Layanan"
            placeholder="instant_bike"
            className="w-full"
          inputClassName="border-gray-300"
            value={formData.serviceCode}
            onChange={handleCodeChange}
          />
          <p className="text-xs text-gray-700 mt-1 ml-1 mb-0">Kode layanan harus unik, hanya alfabet, angka, dan underscore, tanpa ada spasi. Contoh: "instant_bike", "reg", dll.</p>
        </div>

        {/* Description */}
        <Input
          label="Deskripsi"
          placeholder="Deskripsi layanan Anda"
          className="w-full"
          inputClassName="border-gray-300"
          value={formData.description}
          onChange={handleFieldChange("description")}
        />

        {/* Shipping Type */}
        <Select
          label={<span className="text-gray-900 mb-4">Tipe Pengiriman</span>}
          className="w-full"
          options={shippingTypeOptions}
          value={formData.shippingType}
          onChange={handleFieldChange("shippingType")}
        />

        {/* Service Type */}
        <Select
          label={<span className="text-gray-900 mb-4">Tipe Layanan</span>}
          className="w-full"
          options={serviceTypeOptions}
          value={formData.serviceType}
          onChange={handleFieldChange("serviceType")}
        />

        {/* Shipment Duration Range */}
        <div>
          <Input
            label="Rentang Durasi Pengiriman"
            placeholder="1-3"
            className="w-full"
            inputClassName="border-gray-300"
            value={formData.shipmentDurationRange}
            onChange={handleShipmentDurationRangeChange}
          />
          <p className="text-xs text-gray-700 mt-1 ml-1 mb-0">Rentang durasi pengiriman harus berupa angka, tanpa ada spasi. Contoh: "1-3", "4", dll.</p>
        </div>

        {/* Shipment Duration Unit */}
        <Select
          label={<span className="text-gray-900 mb-4">Satuan Durasi Pengiriman</span>}
          className="w-full"
          options={durationUnitOptions}
          value={formData.shipmentDurationUnit}
          onChange={handleFieldChange("shipmentDurationUnit")}
        />

        {/* Collection Methods */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">Pengambilan Paket</label>
          <div className="space-y-2">
            {formData.collectionMethods.map((cm, idx) => (
              <div key={`cm-${idx}`} className="flex items-center space-x-2">
                <div className="flex-1">
                  <Select
                    labelHidden
                    options={collectionMethodOptions}
                    value={cm}
                    onChange={handleCollectionMethodChange(idx)}
                  />
                </div>
                <Button 
                  title="Hapus" 
                  className="md:w-auto px-3" 
                  onClick={handleRemoveCollectionMethod(idx)} 
                  secondary 
                />
              </div>
            ))}
          </div>
          <div className="mt-2">
            <Button
              title="Tambah"
              className="md:w-auto"
              onClick={handleAddCollectionMethod}
              disabled={formData.collectionMethods.length >= COLLECTION_METHOD_VALUES.length}
            />
          </div>
        </div>
      </div>

      <ModalFooter
        primaryButton={{ 
          loading: loading, 
          title: "Simpan", 
          onClick: handleSubmit 
        }}
      />
    </Modal>
  );
}

